import React, { Reducer, useCallback, useEffect, useReducer, useState } from 'react'
import { DateInput } from 'semantic-ui-calendar-react'
import { Form, Grid, Header, Transition } from 'semantic-ui-react'
import { v4 as uuid } from 'uuid'
import { DollarInput } from '../../components/DollarInput'
import { yesterday } from '../../utils/dateUtil'
import ClosingListItem from './components/ClosingListItem'
import { ANIMATION_DURATION } from './constants'
import styles from './styles.module.less'
import { CurrencyEnum, LotMatchingInstruction, TradeFormErrors, TradeFormTouched, TradeFormValues } from './Types'

export interface LotMatchingInstructionsProps {
  errors: TradeFormErrors
  touched: TradeFormTouched
  values: TradeFormValues
  setFieldValue: (field: string, value: any, shouldValidate?: boolean | undefined) => void
}

export enum fieldTypesEnum {
  tradeDate = 'tradeDate',
  price = 'price',
  quantity = 'quantity',
}

type instructionAction = {
  type: 'ADD' | 'REMOVE' | 'UPDATE'
  id?: string
  value?: string
  field?: fieldTypesEnum
}

export const reducer: Reducer<LotMatchingInstruction[], instructionAction> = (
  state: LotMatchingInstruction[],
  action: instructionAction
) => {
  switch (action.type) {
    case 'ADD':
      if (state.length >= 5) {
        return state
      }
      return [...state, { id: uuid() }]
    case 'REMOVE':
      return state.filter(item => item.id !== action.id)
    case 'UPDATE':
      return state.map(item => {
        if (item.id === action.id) {
          if (action.field) {
            return { ...item, [action.field]: action.value }
          }
        }
        return item
      })
    default:
      return state
  }
}

interface FieldTouched {
  [index: string]: boolean
}

const LotMatchingInstructions = ({
  errors,
  touched,
  values: { lotMatchingInstructions },
  setFieldValue,
}: LotMatchingInstructionsProps) => {
  const [instructionsList, dispatch] = useReducer(reducer, lotMatchingInstructions || [])
  const setFieldValueMemoized = useCallback(setFieldValue, [])
  useEffect(() => {
    setFieldValueMemoized('lotMatchingInstructions', instructionsList)
  }, [instructionsList, setFieldValueMemoized])
  const [fieldTouched, touchField] = useState<FieldTouched>({})
  const errorsMap: {
    [index: string]: { tradeDate?: { content: string }; quantity?: { content: string }; price?: { content: string } }
  } = {} // @ts-ignore
  ;(errors['lotMatchingInstructions'] || []).forEach(
    (value: { tradeDate?: string; quantity?: string; price?: string }, index: number) => {
      if (value) {
        errorsMap[`${index}`] = {
          ...(value.tradeDate && { tradeDate: { content: value.tradeDate } }),
          ...(value.quantity && { quantity: { content: value.quantity } }),
          ...(value.price && { price: { content: value.price } }),
        }
      }
    }
  )
  const getFieldId = (id: string, fieldName: string) => `${fieldName}${id}`
  const getError = (index: number, id: string, fieldName: 'tradeDate' | 'quantity' | 'price') => {
    const rowErrors = errorsMap[`${index}`]
    const fieldError =
      rowErrors &&
      (touched['lotMatchingInstructions'] || fieldTouched[getFieldId(id, fieldName)]) &&
      rowErrors[fieldName]
    return fieldError ? fieldError : undefined
  }
  const handleBlur = (id: string, fieldName: string) =>
    touchField(previous => ({ ...previous, [getFieldId(id, fieldName)]: true }))
  return (
    <>
      <Header as={'h3'}>Lot Matching Instructions</Header>
      <p>Add up to five sets of lot-matching instructions.</p>
      <Transition.Group animation={'fade down'} duration={ANIMATION_DURATION}>
        {lotMatchingInstructions?.map(({ id, tradeDate, price, quantity }: LotMatchingInstruction, index: number) => (
          <div key={id}>
            <ClosingListItem onClose={() => dispatch({ type: 'REMOVE', id })} showRemoveButton>
              <Grid columns="equal">
                <Grid.Row>
                  <Grid.Column>
                    <DateInput
                      error={getError(index, id, 'tradeDate')}
                      className={'test-trade-date'}
                      name="tradeDate"
                      label="Trade Date"
                      iconPosition="right"
                      dateFormat="MM/DD/YYYY"
                      placeholder={'MM/DD/YYYY'}
                      required
                      value={tradeDate || ''}
                      maxDate={yesterday()}
                      onBlur={() => handleBlur(id, 'tradeDate')}
                      onChange={(_, data) => {
                        dispatch({ type: 'UPDATE', value: data.value, id, field: fieldTypesEnum.tradeDate })
                      }}
                    />
                  </Grid.Column>
                  <Grid.Column>
                    <Form.Input
                      className={'test-quantity'}
                      error={getError(index, id, 'quantity')}
                      label="Quantity"
                      name="quantity"
                      value={quantity}
                      onBlur={() => handleBlur(id, 'quantity')}
                      onChange={(_, data) => {
                        dispatch({ type: 'UPDATE', value: data.value, id, field: fieldTypesEnum.quantity })
                      }}
                    />
                  </Grid.Column>
                  <Grid.Column>
                    <DollarInput
                      className="test-price"
                      currency={CurrencyEnum.usd}
                      error={getError(index, id, 'price')}
                      name="price"
                      label="Price"
                      onBlur={() => handleBlur(id, 'price')}
                      onChange={(_, data) => {
                        dispatch({ type: 'UPDATE', value: data?.toString(), id, field: fieldTypesEnum.price })
                      }}
                      value={price}
                    />
                  </Grid.Column>
                </Grid.Row>
              </Grid>
            </ClosingListItem>
          </div>
        ))}
        {lotMatchingInstructions && lotMatchingInstructions?.length < 5 && (
          <div className={`${styles.addItemButtonWrap} test-add-lot-ins`}>
            <button onClick={() => dispatch({ type: 'ADD' })} type={'button'} className={styles.btnLink}>
              + Add Item
            </button>
          </div>
        )}
      </Transition.Group>
    </>
  )
}

export default LotMatchingInstructions
